import { GrandstandButton } from '@/components/Buttons'
import LoadingSpinner from '@/components/LoadingSpinner'
import { NavTop, NavTopPortalWrapper } from '@/components/TopNav/NavTop'
import { BaseContainer } from '@/components/containers/BaseContainer'
import AccountDetails from '@/components/settings/AccountDetails'
import Billing from '@/components/settings/Billing'
import ConnectTVProvider from '@/components/settings/ConnectTVProvider'
import Favorites from '@/components/settings/Favorites'
import ManageDevices from '@/components/settings/ManageDevices'
import Packages from '@/components/settings/Packages'
import Purchases from '@/components/settings/Purchases'
import SingleGames from '@/components/settings/SingleGames'
import useMediaQuery from '@/hooks/useMediaQuery'
import { breakpoints } from '@/styles/breakpoints'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { usePurchasingDisabled } from '@grandstand-web/bally-web-shared/src/hooks/usePurchasingDisabled'
import { ApiClient } from '@grandstand-web/bally-web-shared/src/newPackages/ApiClient'
import { getString } from '@grandstand-web/bally-web-shared/src/newPackages/StringMgmt'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import {
  ConfigServiceContext,
  useContextUnconditionally,
} from '@grandstand-web/bally-web-shared/src/services/config/ConfigService'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import {
  ActiveSubscriptionsResponse,
  PaymentMethodsResponse,
  PurchaseHistoryResponse,
} from '@grandstand/presentation-models'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  position: relative;
  color: var(--on-background);
`

const SettingsContainer = styled(BaseContainer)`
  max-width: 1480px;
  ${breakpoints.down('sm')} {
    padding-left: 0;
    padding-right: 0;
  }
`

const ChevronIcon = styled(GrandstandIcon)``

const TopFormButton = styled(GrandstandButton)``

const Settings = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 700px;
  width: 100%;
  background-color: var(--surface);
  ${breakpoints.up('md')} {
    flex-direction: row;
  }
  @media (max-width: 1279px) {
    margin-top: 72px;
  }
`

const SettingsNavigation = styled.div`
  padding: 25px 0px;
  width: 322px;
  @media (max-width: 767px) {
    width: 100%;
  }
  h1,
  span {
    padding-left: 25px;
  }
`

const NavHeader = styled.h1`
  margin-bottom: 30px;
  padding-left: 25px;
`

const NavFooter = styled.span`
  position: absolute;
  bottom: 24px;
  font-size: var(--caption-xs-size);
  padding-left: 25px;
  display: block;
  margin-top: auto;
  word-wrap: break-word;
  color: var(--on-background-alt);
`

const SettingsNavigationItem = styled(Link)<{ $isSelected: boolean }>`
  display: block;
  padding-top: 15px;
  padding-left: ${(props) => (props.$isSelected ? '21px' : '25px')};
  cursor: pointer;
  height: 55px;
  border-left: ${(props) => (props.$isSelected ? '2px solid var(--on-background-alt)' : 'none')};
  div {
    float: right;
    @media (min-width: 768px) {
      display: none;
    }
  }
`

const Content = styled.div`
  overflow: auto;
  border-left: 1px solid var(--divider-canvas);
  flex: 1;
  @media (max-width: 767px) {
    display: none;
  }
`

const MobileContent = styled.div`
  border-left: 1px solid var(--divider-canvas);
  flex: 1;
`

const SettingsPage = () => {
  const userService = useContext(UserServiceContext)
  const analyticService = useContext(AnalyticServiceContext)
  const { currentConfig } = useContextUnconditionally(ConfigServiceContext)
  const isDTCEntitled = userService.isDTCEntitled()
  const isMVPDEntitled = userService.isMVPD
  const [isMobileMenuClicked, setIsMobileMenuClicked] = useState(false)
  const isMobile = useMediaQuery('(max-width: 767px)')
  const router = useRouter()
  var pageName = router.query.pageName ?? 'account'
  const [allSubscriptions, setAllSubscriptions] = useState<ActiveSubscriptionsResponse>([])
  const [creditCardDetail, setCreditCardDetail] = useState<PaymentMethodsResponse[number] | null>(null)
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistoryResponse | null>(null)
  const [userHasFreeTrial, setUserHasFreeTrial] = useState(false)
  if (!userService.isLoggedIn) {
    // however the unauthenticated user got here we should just send them home, bye bye!
    router.push('/')
  }
  const [showSpinner, setShowSpinner] = useState(true)
  const hasPurchases = !!purchaseHistory?.redemption_history.length

  useEffect(() => {
    const getData = async () => {
      const data = await ApiClient.convenientApiFetch({
        url: currentConfig.API.services.subscription_services.subscription,
        method: 'GET',
      })
      const response = await data.json()
      setAllSubscriptions(response)
    }
    getData().finally(() => setShowSpinner(false))
  }, [currentConfig])

  useEffect(() => {
    const fetchPayments = async () => {
      // todo: we do not need to call following code if user is logged/logging out

      const paymentMethods = await ApiClient.convenientApiFetch({
        url: currentConfig.API.services.subscription_services.payment_methods + '?filter=latest-card',
        method: 'GET',
      })
        .then((data) => data.json())
        .then((data) => {
          if (data && data.hasOwnProperty('status')) {
            console.error(data)
            return []
          } else {
            return data as PaymentMethodsResponse
          }
        })

      const cardDetail = paymentMethods?.at(0)
      if (cardDetail) {
        setCreditCardDetail(cardDetail)
      }
    }

    fetchPayments().catch((err) => {
      console.error(err)
    })
  }, [currentConfig, userService, userService.getPayments])

  useEffect(() => {
    const fetchPurchases = async () => {
      const history = await userService.getPurchaseHistory()
      if (history) {
        setPurchaseHistory(history)
      }
    }
    fetchPurchases().catch((err) => {
      console.error(err)
    })
  }, [userService])

  useEffect(() => {
    if (allSubscriptions.length > 0) {
      allSubscriptions.forEach((subscription) => {
        if (subscription.free_trial_start !== null) {
          setUserHasFreeTrial(true)
        }
      })
    }
  }, [allSubscriptions])

  if (typeof pageName !== 'string') {
    pageName = pageName[0] ?? 'account'
  }

  const handleMenuItemClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (isMobile) {
      const target = event.target as HTMLElement
      setIsMobileMenuClicked(true)
    }
  }

  const renderComponent = (pageName: string) => {
    switch (pageName) {
      case 'account':
        return (
          <AccountDetails
            subscriptions={allSubscriptions}
            onBack={() => {
              setIsMobileMenuClicked(false)
            }}
          />
        )
      case 'favorites':
        return (
          <Favorites
            onBack={() => {
              setIsMobileMenuClicked(false)
            }}
          />
        )
      case 'devices':
        return (
          <ManageDevices
            onBack={() => {
              setIsMobileMenuClicked(false)
            }}
          />
        )
      case 'purchases':
        return (
          <Purchases
            subscriptions={allSubscriptions}
            isDTCEntitled={isDTCEntitled}
            hasPurchases={hasPurchases}
            availableCredits={userService.availableCredits()}
            onBack={() => {
              setIsMobileMenuClicked(false)
            }}
          />
        )
      case 'packages':
        return (
          <Packages
            creditCardDetail={creditCardDetail}
            subscriptions={allSubscriptions}
            isDTCEntitled={isDTCEntitled}
          />
        )
      case 'single-games':
        return (
          <SingleGames
            hasPurchases={hasPurchases}
            availableCredits={userService.availableCredits()}
            purchaseHistory={purchaseHistory}
          />
        )
      case 'billing':
        return <Billing creditCardDetail={creditCardDetail} subscriptions={allSubscriptions} />
      case 'tvprovider':
        return (
          <ConnectTVProvider
            onBack={() => {
              setIsMobileMenuClicked(false)
            }}
          />
        )
      default:
        return (
          <AccountDetails
            subscriptions={allSubscriptions}
            onBack={() => {
              setIsMobileMenuClicked(false)
            }}
          />
        )
    }
  }

  const handleGetBallySportsPlusClick = () => {
    analyticService.clickGetBallySportsPlus(
      getString('onboarding.authentication.getBallyPlus.cta') ?? 'Get FanDuel Sports Network'
    )
    router.push('/packages')
  }

  const NavTopEndItem = () => {
    const { isPurchasingDisabled } = usePurchasingDisabled()
    if (isPurchasingDisabled) {
      return <></>
    }
    return (
      <TopFormButton buttonStyle="primary" buttonSize="sm" onClick={() => handleGetBallySportsPlusClick()}>
        {getString('onboarding.authentication.getBallyPlus.cta') ?? 'Get FanDuel Sports Network'}
      </TopFormButton>
    )
  }
  return (
    <Page>
      {showSpinner ? (
        <LoadingSpinner />
      ) : (
        <NavTopPortalWrapper>
          {!userHasFreeTrial && !isDTCEntitled && !isMVPDEntitled ? (
            <NavTop endItem={<NavTopEndItem />} containerMaxWidth="1480px" />
          ) : (
            <NavTop containerMaxWidth="1480px" />
          )}
        </NavTopPortalWrapper>
      )}
      <SettingsContainer padded={true}>
        <Settings>
          {!isMobileMenuClicked && (
            <SettingsNavigation onClick={handleMenuItemClick}>
              <div>
                <NavHeader>Settings</NavHeader>
                <SettingsNavigationItem href={'/settings/account'} $isSelected={pageName == 'account'}>
                  Account Details
                  <ChevronIcon icon="chevron_right" />
                </SettingsNavigationItem>
                <SettingsNavigationItem href={'/settings/favorites'} $isSelected={pageName === 'favorites'}>
                  Favorites <ChevronIcon icon="chevron_right" />
                </SettingsNavigationItem>
                <SettingsNavigationItem href={'/settings/devices'} $isSelected={pageName === 'devices'}>
                  Manage Devices <ChevronIcon icon="chevron_right" />
                </SettingsNavigationItem>
                {!isMVPDEntitled || hasPurchases ? (
                  <SettingsNavigationItem href={'/settings/purchases'} $isSelected={pageName === 'purchases'}>
                    Purchases <ChevronIcon icon="chevron_right" />
                  </SettingsNavigationItem>
                ) : (
                  ''
                )}
                <SettingsNavigationItem href={'/settings/tvprovider'} $isSelected={pageName === 'tvprovider'}>
                  TV Provider <ChevronIcon icon="chevron_right" />
                </SettingsNavigationItem>
              </div>
              <NavFooter>
                <Link
                  href={`${getString('more.account.help.url') ?? 'https://help.fanduelsportsnetwork.com/'}`}
                  target="_blank"
                >
                  If you are having issues, contact support at
                  <br /> help.fanduelsportsnetwork.com
                </Link>
              </NavFooter>
            </SettingsNavigation>
          )}
          {isMobileMenuClicked && isMobile && <MobileContent>{renderComponent(pageName)}</MobileContent>}
          <Content>{renderComponent(pageName)}</Content>
        </Settings>
      </SettingsContainer>
    </Page>
  )
}
export default SettingsPage
